import { DateTime } from "luxon";

import type { Calendar, FullCalendarEvent } from "@/Modules/Calendars/Models/Domain";
import type { CalendarEvent, CalendarType } from "@schedule-x/calendar";
import { Language, Locale } from "@/Common/Models/Domain/Language";

import { hasValue } from "@/Utils/Objects";

export const getDateTime = (seconds: number): DateTime => {
  return DateTime.fromSeconds(seconds, { zone: "UTC" });
};

export const transformFullCalendarEventToCalendarEvent = (event: FullCalendarEvent): CalendarEvent => {
  const endDate = getDateTime(event.endAt);
  const startDate = getDateTime(event.startAt);
  const nowDate = DateTime.now();
  const additionalClasses = [
    `calendar-type--${event.calendar.type}`,
    `event--calendar-${event.calendar.uuid}`,
  ];

  if (nowDate.toUnixInteger() >= event.endAt) {
    additionalClasses.push("event--past");
  } else if (endDate.hasSame(nowDate, "day")) {
    additionalClasses.push("event--today");
  }

  return {
    id: event.uuid,
    title: event.summary,
    people: hasValue(event.patient) ? [event.patient.username] : [],
    start: dateTimeToPlainDateTime(startDate).toZonedDateTime("UTC"),
    end: dateTimeToPlainDateTime(endDate).toZonedDateTime("UTC"),
    calendarId: event.calendarUuid,
    _options: {
      additionalClasses,
    },
  };
};

export const transformCalendarToCalendarType = (calendar: Calendar): CalendarType => {
  return {
    colorName: calendar.uuid,
    label: calendar.name,
    lightColors: {
      container: calendar.backgroundColor,
      main: `color-mix(in srgb, ${calendar.backgroundColor}, black 40%)`,
      onContainer: calendar.foregroundColor,
    },
  };
};

export const getLocale = (lang: Language): string => {
  switch (lang) {
    case Language.ENGLISH:
      return Locale.EN;
    case Language.SPANISH:
      return Locale.ES;
    case Language.ITALIAN:
      return Locale.IT;
    case Language.PORTUGUESE:
      return Locale.PT;
    default:
      return Locale.ES;
  }
};

export const closestNumber = (target: number, numbers: number[]): number | null => {
  if (numbers.length === 0) return null;

  return numbers.reduce((closest, current) =>
    Math.abs(current - target) < Math.abs(closest - target) ? current : closest
  );
};

export const dateTimeToPlainDate = (dateTime: DateTime): Temporal.PlainDate => {
  return Temporal.PlainDate.from(dateTime.toISO()!);
};

export const dateTimeToPlainDateTime = (dateTime: DateTime): Temporal.PlainDateTime => {
  const inst = Temporal.Instant.fromEpochMilliseconds(dateTime.toMillis());

  return inst.toZonedDateTimeISO("UTC").toPlainDateTime();
};

export const plainDateToDateTime = (date: Temporal.PlainDate): DateTime => {
  return DateTime.fromISO(date.toString());
};

export const zonedPlainDateTimeToDateTime = (date: Temporal.ZonedDateTime): DateTime => {
  return DateTime.fromISO(date.toString(), { zone: date.timeZoneId }).toUTC();
};
